import {
  useAdminSponsoredLists,
  useCatalogAdminAccess,
  useUnreadNotificationCount,
} from '@lista/app-core';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { useSession } from '../auth/session-store';
import { useAuthActions } from '../auth/use-auth-actions';
import { Icon, type IconName, Menu, type MenuItem } from '../ui';
import { initials } from '../lib/format';
import { OfflineBanner } from './OfflineBanner';
import { TopbarSlotContext } from './TopbarSlot';
import styles from './layout.module.css';

const NAV: Array<{ to: string; key: string; icon: IconName; end?: boolean }> = [
  { to: '/', key: 'nav.lists', icon: 'lists', end: true },
  { to: '/categories', key: 'nav.categories', icon: 'grid' },
  { to: '/meals', key: 'nav.meals', icon: 'meals' },
  { to: '/search', key: 'nav.search', icon: 'search' },
  { to: '/favorites', key: 'nav.favorites', icon: 'star' },
];

/**
 * Responsive shell (frontend.md → Responsive layout): top bar + bottom navigation on phones
 * (the avatar opens the account menu), navigation rail on tablets, sidebar on desktop.
 */
export function AppShell() {
  const { t } = useTranslation();
  const user = useSession((s) => s.user);
  const unread = useUnreadNotificationCount();
  // The API decides: a 403 simply means this account has no admin entry.
  const isAdmin = useAdminSponsoredLists().isSuccess;
  const isCatalogAdmin = useCatalogAdminAccess().isSuccess;
  const navigate = useNavigate();
  const { signOut } = useAuthActions();
  const [topbarSlot, setTopbarSlot] = useState<HTMLDivElement | null>(null);
  // Phones: the avatar opens everything that is not in the bottom bar.
  const accountItems: MenuItem[] = [
    // One "Admin" entry: the hub page links to whatever this account may manage.
    {
      label: t('nav.admin'),
      icon: 'shield',
      hidden: !isAdmin && !isCatalogAdmin,
      onSelect: () => navigate('/admin'),
    },
    { label: t('nav.profile'), icon: 'user', onSelect: () => navigate('/profile') },
    { label: t('nav.settings'), icon: 'settings', onSelect: () => navigate('/settings') },
    { label: t('profile.signOut'), icon: 'logout', onSelect: () => void signOut() },
  ];

  return (
    <div className={styles.shell}>
      <a className={styles.skip} href="#main">
        {t('a11y.skipToContent')}
      </a>

      <header className={styles.topbar}>
        <Link to="/" className={styles.brand}>
          Lista
        </Link>
        <div ref={setTopbarSlot} className={styles.topbarSlot} />
        <div className={styles.topbarTools}>
          <NotificationsLink unread={unread} className={styles.bell} />
          <Menu
            label={t('nav.accountMenu')}
            avatar={initials(user?.displayName ?? '')}
            items={accountItems}
          />
        </div>
      </header>

      <nav className={styles.nav} aria-label={t('nav.label')}>
        <Link to="/" className={styles.navBrand}>
          Lista
        </Link>
        <ul className={styles.navList}>
          {NAV.map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} end={item.end} className={styles.link}>
                <Icon name={item.icon} size={22} />
                <span className={styles.label}>{t(item.key)}</span>
              </NavLink>
            </li>
          ))}
        </ul>
        <ul className={[styles.navList, styles.navSecondary].join(' ')}>
          <li>
            <NavLink to="/notifications" className={styles.link}>
              <span className={styles.iconWithBadge}>
                <Icon name="bell" size={22} />
                <UnreadBadge count={unread} />
              </span>
              <span className={styles.label}>{t('nav.notifications')}</span>
            </NavLink>
          </li>
          {(isAdmin || isCatalogAdmin) && <AdminNav sponsored={isAdmin} catalog={isCatalogAdmin} />}
          <li>
            <NavLink to="/profile" className={styles.link}>
              <Icon name="user" size={22} />
              <span className={styles.label}>{t('nav.profile')}</span>
            </NavLink>
          </li>
          <li>
            <NavLink to="/settings" className={styles.link}>
              <Icon name="settings" size={22} />
              <span className={styles.label}>{t('nav.settings')}</span>
            </NavLink>
          </li>
        </ul>
      </nav>

      <main id="main" className={styles.main} tabIndex={-1}>
        <OfflineBanner />
        <TopbarSlotContext.Provider value={topbarSlot}>
          <Outlet />
        </TopbarSlotContext.Provider>
      </main>
    </div>
  );
}

/**
 * Sidebar "Admin" group: a disclosure button with a link per area this account may manage.
 * It opens by itself on any /admin page.
 */
function AdminNav({ sponsored, catalog }: { sponsored: boolean; catalog: boolean }) {
  const { t } = useTranslation();
  const inAdmin = useLocation().pathname.startsWith('/admin');
  const [open, setOpen] = useState(inAdmin);
  useEffect(() => {
    if (inAdmin) setOpen(true);
  }, [inAdmin]);
  return (
    <li>
      <button
        type="button"
        className={styles.link}
        aria-expanded={open}
        aria-controls="nav-admin"
        data-active={inAdmin || undefined}
        onClick={() => setOpen((o) => !o)}
      >
        <Icon name="shield" size={22} />
        <span className={styles.label}>{t('nav.admin')}</span>
        <Icon name="expand" size={16} className={styles.expand} />
      </button>
      <ul id="nav-admin" className={styles.subNav} hidden={!open}>
        {sponsored && (
          <li>
            <NavLink to="/admin/sponsored" className={styles.link}>
              <Icon name="sparkles" size={20} />
              <span className={styles.label}>{t('nav.sponsoredAdmin')}</span>
            </NavLink>
          </li>
        )}
        {catalog && (
          <li>
            <NavLink to="/admin/catalog" className={styles.link}>
              <Icon name="grid" size={20} />
              <span className={styles.label}>{t('nav.catalogAdmin')}</span>
            </NavLink>
          </li>
        )}
      </ul>
    </li>
  );
}

function UnreadBadge({ count }: { count: number }) {
  const { t } = useTranslation();
  if (count <= 0) return null;
  return (
    <span
      className={styles.unreadBadge}
      role="status"
      aria-label={t('notifications.unreadBadge', { count })}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/** Phone top bar: bell with the number of unread notifications. */
function NotificationsLink({ unread, className }: { unread: number; className?: string }) {
  const { t } = useTranslation();
  return (
    <Link to="/notifications" className={className} aria-label={t('notifications.openLabel')}>
      <span className={styles.iconWithBadge}>
        <Icon name="bell" size={22} />
        <UnreadBadge count={unread} />
      </span>
    </Link>
  );
}
